/**
 * Condition schemas — the v0 condition grammar (decision D15).
 *
 * @remarks
 * Conditions gate exits, item visibility, and use-interactions; `engine-runtime`
 * evaluates them exhaustively in R5. The union is discriminated on `type` so
 * validation errors carry precise paths (architecture.md §5) and evaluators
 * narrow with `switch` + `never` guards (architecture.md §3.5). Growth
 * variants (scheduler/ending/puzzle predicates) arrive with their phases as
 * compile-checked union extensions.
 *
 * **Documented deviation from "inferred types only" (architecture.md §5):**
 * the condition family is the one RECURSIVE schema in the package, and Zod
 * 4.4 cannot infer a recursive `discriminatedUnion` (the getter idiom
 * type-checks for plain objects but collapses to `any` through the
 * discriminated-union constraint — verified 2026-08-12). The variant types
 * below are therefore hand-written, and the `z.ZodType<ConditionDefinition>`
 * annotation on {@link conditionSchema} keeps them honest: any drift between
 * the schema's output and these types is a compile error at the annotation
 * site, not a runtime surprise.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import type {
	ActorAddressableCondition,
	ActorAttackedCondition,
	ActorDefeatedCondition,
	ActorInRoomCondition,
	ActorPresentCondition,
	PlayerHealthAtLeastCondition,
} from './actor-conditions.js';
import type { ItemFoundCondition, ItemSearchedCondition } from './discovery-conditions.js';
import type { RecordHoldsCondition } from './record-conditions.js';
import type { DirectObjectIsCondition, IndirectObjectIsCondition, ToolIsCondition } from './sentence-conditions.js';

import { atLeastOfComplaint } from './at-least-of-complaint.js';
import { type CounterAtLeastCondition, counterAtLeastConditionSchema } from './counter-conditions.js';
import {
	actorIdSchema,
	type EndingId,
	endingIdSchema,
	type EventDefinitionId,
	eventDefinitionIdSchema,
	type FlagId,
	flagIdSchema,
	type ItemId,
	itemIdSchema,
	type LogId,
	logIdSchema,
	type RoomId,
	roomIdSchema,
	type VoiceLineId,
	voiceLineIdSchema,
} from './identifiers.js';
import { nonBlankStringSchema, positiveIntegerSchema } from './primitives.js';

// The three conditions that read the SENTENCE rather than the world live in
// their own module (D229), and (D234, at the 500-line cap) so do the five that
// read a BODY, and (D239, at the cap again) the two that read DISCOVERY, and
// (D241) the one that reads the RECORD; they are members of the union below
// all the same.
export type { ActorAddressableCondition, ActorAttackedCondition, ActorDefeatedCondition, ActorInRoomCondition, ActorPresentCondition, PlayerHealthAtLeastCondition } from './actor-conditions.js';
export type { ItemFoundCondition, ItemSearchedCondition } from './discovery-conditions.js';
export type { CounterAtLeastCondition } from './counter-conditions.js';
export type { RecordHoldsCondition, RecordLayerKind } from './record-conditions.js';
export type { DirectObjectIsCondition, IndirectObjectIsCondition, ToolIsCondition } from './sentence-conditions.js';

/** Satisfied when EVERY nested condition is satisfied (logical AND; at least one required). */
export interface AllOfCondition {
	readonly conditions: readonly ConditionDefinition[];
	readonly type: 'all_of';
}

/** Satisfied when ANY nested condition is satisfied (logical OR; at least one required). */
export interface AnyOfCondition {
	readonly conditions: readonly ConditionDefinition[];
	readonly type: 'any_of';
}

/**
 * D229 (E23): satisfied when at least `minimumCount` of the nested conditions
 * are satisfied — "at least two of the four jars sealed", as one node.
 *
 * @remarks
 * The general combinator the other two are sugar for: `all_of` is n of n and
 * `any_of` is 1 of n. Before it, Fever Ground spelled "at least two sealed"
 * as the six pairs under an `any_of` and "at least three" as the four
 * triples, in every reading that needed them, and the pack's document
 * doubled on those expansions alone. The degenerate spellings are refused
 * at the schema, so one reading has one spelling: a count of 1 is `any_of`,
 * a count equal to the members is `all_of`, and a count above them can
 * never hold — authored content nothing can reach (D31). `minimumCount`
 * is the name `occurrence_tally_at_least` already uses for the same idea.
 *
 * The static walks read it as they read `any_of`: every member is judged
 * with the polarity of the wrappers above (`condition-producibility`), and
 * every reference inside it is checked like any other.
 */
export interface AtLeastOfCondition {
	readonly conditions: readonly ConditionDefinition[];
	readonly minimumCount: number;
	readonly type: 'at_least_of';
}

/**
 * Satisfied when the named thing's door is currently OPEN (D55).
 *
 * @remarks
 * A thing with no door is always open, because there is nothing to be shut —
 * the same reading `deriveEntityCatalog` has used since D50, stated once and
 * shared rather than re-decided here.
 *
 * It exists so that conditional text can say what `openable`'s per-state
 * descriptions used to say, which is what lets those descriptions become sugar
 * over this rather than a second mechanism beside it.
 */
export interface ContainerOpenCondition {
	readonly itemId: ItemId;
	readonly type: 'container_open';
}

/**
 * Satisfied when an ending has been selected (R11) — the named one, or ANY
 * ending when `endingId` is omitted.
 *
 * @remarks
 * Reaching an ending is a state fact other content can react to — §7.3 lists
 * `ending` among the trigger matchers — so epilogue chrome, post-ending
 * events, and "the story is over" gates all read this rather than inventing
 * flags that shadow `narrative.selectedEndingIds`. With an `endingId` it asks
 * whether THAT ending is in the history; without one, whether any is (D151).
 */
export interface EndingSelectedCondition {
	readonly endingId?: EndingId;
	readonly type: 'ending_selected';
}

/**
 * Satisfied when the named event has FIRED at least once this session (R11).
 *
 * @remarks
 * Reads `scheduler.firedEventIds` — fired, not merely processed: a dropped
 * occurrence changed nothing and promises nothing. This is the predicate
 * Calder's pump-plus-sensor pattern (his 2026-08-22 §4.1) had to fake with a
 * dedicated flag; now time's own audit trail is authorable.
 */
export interface EventFiredCondition {
	readonly eventId: EventDefinitionId;
	readonly type: 'event_fired';
}

/** Satisfied when the named flag currently holds `expectedValue`. */
export interface FlagEqualsCondition {
	readonly expectedValue: boolean;
	readonly flagId: FlagId;
	readonly type: 'flag_equals';
}

/** Satisfied when the named item is in the player's inventory. */
export interface HasItemCondition {
	readonly itemId: ItemId;
	readonly type: 'has_item';
}

/**
 * Satisfied when the named item has EVER been taken (D146) — a read of R25's
 * write-once acquisition record, which is a fact about history rather than
 * about where the thing is now. "The mask is no longer on the hook" is this
 * exactly, and `item_in_room` cannot say it: that means AT ITS AUTHORED
 * PLACEMENT (D112), which is never true of a contained item. A thing dropped
 * elsewhere was still taken; a thing never taken is still on its hook.
 */
export interface ItemAcquiredCondition {
	readonly itemId: ItemId;
	readonly type: 'item_acquired';
}

/**
 * Satisfied when the named item is currently EQUIPPED (D143) — worn, wielded,
 * in its slot — as distinct from merely carried. The mask stops the gas only
 * on your face; the knife cuts only in your hand.
 */
export interface ItemEquippedCondition {
	readonly itemId: ItemId;
	readonly type: 'item_equipped';
}

/**
 * Satisfied when the named item is SPENT (D152) — its `durability.uses` all
 * gone. The broken strap is a state fact other content can read: a
 * description fragment, her line, a trigger that reacts to the crossing.
 */
export interface ItemSpentCondition {
	readonly itemId: ItemId;
	readonly type: 'item_spent';
}

/** Satisfied when the named item is currently placed in the named room. */
export interface ItemInRoomCondition {
	readonly itemId: ItemId;
	readonly roomId: RoomId;
	readonly type: 'item_in_room';
}

/**
 * Satisfied while the named event has an occurrence WAITING on the scheduler
 * (D156) — armed and not yet fired.
 *
 * @remarks
 * `event_fired` asks whether a thing has happened; this asks whether it is
 * still coming. D153's clock is the case that wanted it: "is the countdown
 * running" was only answerable through a flag maintained in parallel with the
 * scheduler, which is a second copy of a fact the engine already holds.
 *
 * Any pending occurrence counts, and `cancel_event` drops them all, so this
 * goes false the moment the chain is cut. One shape is refused at load: an
 * event naming ITSELF here, because the tick consumes the occurrence before
 * evaluating that event's own conditions — it would be constant-false, which
 * is authored content nothing can reach.
 */
export interface EventPendingCondition {
	readonly eventId: EventDefinitionId;
	readonly type: 'event_pending';
}

/** Satisfied when the named log has been read at least once (R12). */
export interface LogReadCondition {
	readonly logId: LogId;
	readonly type: 'log_read';
}

/**
 * Satisfied when an engine-maintained occurrence tally has reached the
 * minimum (R12) — the "respond to repeated behavior" predicate.
 *
 * @remarks
 * Tally keys are the occurrence type, optionally narrowed with a colon:
 * `parse_failed`, `parse_failed:gibberish_input`, `command_rejected:exit_locked`,
 * `command_executed:wait`, `reaction_spoken:xyzzy`. The engine maintains
 * every tally whether or not anything reads it, so escalation is authored as
 * several once-only triggers gated at rising counts.
 */
export interface OccurrenceTallyAtLeastCondition {
	readonly minimumCount: number;
	readonly tallyKey: string;
	readonly type: 'occurrence_tally_at_least';
}

/** Satisfied when the named voice line has been SPOKEN at least once (R12). */
export interface VoiceLineSpokenCondition {
	readonly type: 'voice_line_spoken';
	readonly voiceLineId: VoiceLineId;
}

/** Satisfied when the nested condition is NOT satisfied (logical negation). */
export interface NotCondition {
	readonly condition: ConditionDefinition;
	readonly type: 'not';
}

/** Satisfied when the named thing's switch is currently ON (D56). */
export interface SwitchedOnCondition {
	readonly itemId: ItemId;
	readonly type: 'switched_on';
}

/** Satisfied when the player's active room is the named room. */
export interface RoomEqualsCondition {
	readonly roomId: RoomId;
	readonly type: 'room_equals';
}

/** The v0 condition grammar (D15) — a discriminated union on `type`. */
export type ConditionDefinition =
	| ActorAddressableCondition
	| ActorAttackedCondition
	| ActorDefeatedCondition
	| ActorInRoomCondition
	| ActorPresentCondition
	| AllOfCondition
	| AnyOfCondition
	| AtLeastOfCondition
	| ContainerOpenCondition
	| CounterAtLeastCondition
	| DirectObjectIsCondition
	| EndingSelectedCondition
	| EventFiredCondition
	| EventPendingCondition
	| FlagEqualsCondition
	| HasItemCondition
	| IndirectObjectIsCondition
	| ItemAcquiredCondition
	| ItemEquippedCondition
	| ItemFoundCondition
	| ItemInRoomCondition
	| ItemSearchedCondition
	| ItemSpentCondition
	| LogReadCondition
	| NotCondition
	| OccurrenceTallyAtLeastCondition
	| PlayerHealthAtLeastCondition
	| RecordHoldsCondition
	| RoomEqualsCondition
	| SwitchedOnCondition
	| ToolIsCondition
	| VoiceLineSpokenCondition;

/**
 * Validates one authored condition of any variant, recursively. Parsed output
 * is deep-frozen; the `z.ZodType` annotation pins the output type to the
 * hand-written {@link ConditionDefinition} family (see the module remarks for
 * why this one schema is annotated rather than inferred).
 */
export const conditionSchema: z.ZodType<ConditionDefinition> = z
	.discriminatedUnion('type', [
		// D232: the two answers to "is the actor here" — speakable-to, and within reach.
		z.strictObject({
			actorId: actorIdSchema,
			type: z.literal('actor_addressable'),
		}),
		// D234: struck ever, and fallen now — the two facts a fight writes.
		z.strictObject({
			actorId: actorIdSchema,
			type: z.literal('actor_attacked'),
		}),
		z.strictObject({
			actorId: actorIdSchema,
			type: z.literal('actor_defeated'),
		}),
		// D233: where a body STANDS — a fact about the world, read from anywhere.
		z.strictObject({
			actorId: actorIdSchema,
			roomId: roomIdSchema,
			type: z.literal('actor_in_room'),
		}),
		z.strictObject({
			actorId: actorIdSchema,
			type: z.literal('actor_present'),
		}),
		z.strictObject({
			conditions: z
				.array(z.lazy(() => conditionSchema))
				.min(1)
				.readonly(),
			type: z.literal('all_of'),
		}),
		z.strictObject({
			conditions: z
				.array(z.lazy(() => conditionSchema))
				.min(1)
				.readonly(),
			type: z.literal('any_of'),
		}),
		z
			.strictObject({
				conditions: z
					.array(z.lazy(() => conditionSchema))
					.min(2)
					.readonly(),
				minimumCount: positiveIntegerSchema,
				type: z.literal('at_least_of'),
			})
			// D229: one reading, one spelling — and never a count the members cannot reach.
			.superRefine((condition, context) => {
				const complaint = atLeastOfComplaint(condition.minimumCount, condition.conditions.length);

				if (complaint !== undefined) {
					context.addIssue({ code: 'custom', message: complaint, path: ['minimumCount']});
				}
			}),
		z.strictObject({
			itemId: itemIdSchema,
			type: z.literal('container_open'),
		}),
		counterAtLeastConditionSchema, // D244: an amount the world holds, at a threshold.
		z.strictObject({
			endingId: endingIdSchema.optional(),
			type: z.literal('ending_selected'),
		}),
		z.strictObject({
			eventId: eventDefinitionIdSchema,
			type: z.literal('event_fired'),
		}),
		z.strictObject({
			eventId: eventDefinitionIdSchema,
			type: z.literal('event_pending'),
		}),
		z.strictObject({
			expectedValue: z.boolean(),
			flagId: flagIdSchema,
			type: z.literal('flag_equals'),
		}),
		z.strictObject({
			itemId: itemIdSchema,
			type: z.literal('has_item'),
		}),
		z.strictObject({
			itemId: itemIdSchema.optional(),
			type: z.literal('direct_object_is'),
		}),
		z.strictObject({
			itemId: itemIdSchema.optional(),
			type: z.literal('indirect_object_is'),
		}),
		z.strictObject({
			itemId: itemIdSchema,
			type: z.literal('item_acquired'),
		}),
		z.strictObject({
			itemId: itemIdSchema,
			type: z.literal('item_equipped'),
		}),
		// D239: the concealed thing found — its container gone through.
		z.strictObject({
			itemId: itemIdSchema,
			type: z.literal('item_found'),
		}),
		z.strictObject({
			itemId: itemIdSchema,
			type: z.literal('item_searched'),
		}),
		z.strictObject({
			itemId: itemIdSchema,
			type: z.literal('item_spent'),
		}),
		z.strictObject({
			itemId: itemIdSchema,
			roomId: roomIdSchema,
			/*
			 * D112 (Calder's R32 §4): "in room" means AT ITS AUTHORED PLACEMENT —
			 * a dropped item is in the room and not in its place, and this
			 * condition stays false for it. That is the right semantics for a
			 * fixed-placement sprite (the picture must not teleport back onto the
			 * shelf when the kettle is dropped by the door) and the surprising one
			 * for the plain reading of the name — hence this line. A condition
			 * about where a movable object is NOW does not exist yet, and should
			 * not be invented until something needs it.
			 */
			type: z.literal('item_in_room'),
		}),
		z.strictObject({
			logId: logIdSchema,
			type: z.literal('log_read'),
		}),
		z.strictObject({
			condition: z.lazy(() => conditionSchema),
			type: z.literal('not'),
		}),
		z.strictObject({
			minimumCount: positiveIntegerSchema,
			tallyKey: nonBlankStringSchema,
			type: z.literal('occurrence_tally_at_least'),
		}),
		// D244: what is left of the player's body, at a threshold — the autodoc's question.
		z.strictObject({ minimumCount: positiveIntegerSchema, type: z.literal('player_health_at_least') }),
		// D241: the Record this run began with — earlier runs' endings and remembered flags.
		z.strictObject({
			endingId: endingIdSchema.optional(),
			kind: z.enum(['death', 'ending']).optional(),
			minimumCount: positiveIntegerSchema.optional(),
			type: z.literal('record_holds'),
			where: z.lazy(() => conditionSchema).optional(),
		}),
		z.strictObject({
			roomId: roomIdSchema,
			type: z.literal('room_equals'),
		}),
		z.strictObject({
			itemId: itemIdSchema,
			type: z.literal('switched_on'),
		}),
		z.strictObject({
			itemId: itemIdSchema.optional(),
			type: z.literal('tool_is'),
		}),
		z.strictObject({
			type: z.literal('voice_line_spoken'),
			voiceLineId: voiceLineIdSchema,
		}),
	])
	.readonly();
